import React from 'react';

// Define a type for the segment
interface PieSegment {
  path: string;
  fill: string;
  labelX: number;
  labelY: number;
  labelAngle: number;
  label: string;
  multiLineLabel?: string[]; // Support for multi-line labels
  fillOpacity: number;
}

interface ConnectingLine {
  path: string;
  color: string;
  label: string;
  labelX: number;
  labelY: number;
}

export default function ConnectedPieCharts() {
  // Chart dimensions
  const width = 1000;
  const height = 700;
  const margin = { top: 40, right: 100, bottom: 0, left: 100 };
  
  // Left side charts positioning (vertically stacked)
  const leftCenterX = width / 3.8;
  const stackSpacing = height / 3.5;
  
  const leftCenterY1 = stackSpacing;
  const leftCenterY2 = stackSpacing * 2;
  const leftCenterY3 = stackSpacing * 3;
  
  // Right chart positioning
  const rightCenterX = (width * 2.6) / 3.3;
  const rightCenterY = height / 3.5*2;
  
  // Radius values for all charts
  const chartRadius = Math.min(width / 2, height) / 6;
  
  // Define the structure with percentages of total radius
  const outerRingPercent = 0.15;   // 30% for outer ring
  const innerRingPercent = 0.3;   // 30% for inner ring
  const centerGapPercent = 0.55;   // 40% for center gap
  
  // Calculate actual radii
  const outerRingOuter = chartRadius;                              // 100% 
  const outerRingInner = chartRadius * (1 - outerRingPercent);     // 70%
  const innerRingOuter = chartRadius * innerRingPercent + chartRadius * centerGapPercent;  // 30%
  const innerRingInner = chartRadius * centerGapPercent;           // 0%

  const chartRadiusChip = Math.min(width / 2, height) / 6;
  const outerRingPercentChip = 0.45;   // 30% for outer ring
  const outerRingOuterChip = chartRadiusChip;                              // 100% 
  const outerRingInnerChip = chartRadius * (1 - outerRingPercentChip);     // 70%
  // Placeholder data for the outer rings of the charts
  // Left Chart 1 (top)
  const dataLeft1 = [
    { label: "SK Hynix", value: 45, tooltip: "Description A" },
    { label: "Micron", value: 35, tooltip: "Description B" },
    { label: "Samsung", value: 40, tooltip: "Description C" },
  ];

  // Left Chart 2 (middle)
  const dataLeft2 = [
    { label: "TSMC", value: 85, tooltip: "Description D" },
    { label: "Others", value: 15, tooltip: "Description E" }
  ];

  // Left Chart 3 (bottom)
  const dataLeft3 = [
    { label: "TSMC", value: 90, tooltip: "Description G" },
    { label: "Others", value: 10, tooltip: "Description H" }
  ];

  // Right Chart
  const dataRight = [
    { label: "Nvidia", value: 58, tooltip: "Description X" },
    { label: "Google", value: 15, tooltip: "Description Y" },
    { label: "Amazon", value: 10, tooltip: "Description Z" },
    { label: "AMD", value: 5, tooltip: "Description Z" },
    { label: "", value: 3, tooltip: "Description Z" },
    { label: "", value: 2, tooltip: "Description Z" },
  ];
  
  // Data for inner rings - always AI vs non-AI
  const innerRingData1 = [
    { label: "AI", value: 95 },
    { label: "Non-AI", value: 5 }
  ];
  
  const innerRingData2 = [
    { label: "AI", value: 5 },
    { label: "Non-AI", value: 95 }
  ];
  
  const innerRingData3 = [
    { label: "AI", value: 90 },
    { label: "Non-AI", value: 10 }
  ];
  
  const innerRingDataRight = [
    { label: "AI", value: 0 },
    { label: "Non-AI", value: 0 }
  ];

  // Chart titles
  const chartTitles = [
    { x: leftCenterX, y: leftCenterY1 - chartRadius - 30, title: "Chip manufacturing" },
    { x: leftCenterX, y: leftCenterY2 - chartRadius - 30, title: "" },
    { x: leftCenterX, y: leftCenterY3 - chartRadius - 30, title: "" },
    { x: rightCenterX, y: rightCenterY - chartRadius - 40, title: "Chip Design" },
  ];

  // Create pie chart paths function - modified for dual ring structure with center gap
  const generatePieSegments = (
    data: Array<{label: string, value: number}>, 
    centerX: number,
    centerY: number,
    isOuterRing: boolean, // Flag to determine if we're generating outer or inner ring
    isChip: boolean
  ) => {
    let currentAngle = 0;
    const segments: PieSegment[] = [];
    
    // Calculate total for percentage calculations
    const total = data.reduce((sum, item) => sum + item.value, 0);

    // Use appropriate radii based on ring type
// In the generatePieSegments function, update the radius determination:
    const useOuterRadius = isOuterRing 
    ? (isChip ? outerRingOuterChip : outerRingOuter) 
    : (isChip ? 0 : innerRingOuter);
    
    const useInnerRadius = isOuterRing 
    ? (isChip ? outerRingInnerChip : outerRingInner) 
    : (isChip ? 0 : innerRingInner);
    data.forEach((item, index) => {
      // Calculate segment angles
      const angleSize = (item.value / total) * 2 * Math.PI;
      const startAngle = currentAngle - Math.PI / 2; // Start from the top
      const endAngle = startAngle + angleSize;
      
      // Calculate outer arc coordinates
      const startX = centerX + Math.cos(startAngle) * useOuterRadius;
      const startY = centerY + Math.sin(startAngle) * useOuterRadius;
      const endX = centerX + Math.cos(endAngle) * useOuterRadius;
      const endY = centerY + Math.sin(endAngle) * useOuterRadius;
      
      // Calculate inner arc coordinates
      const innerStartX = centerX + Math.cos(startAngle) * useInnerRadius;
      const innerStartY = centerY + Math.sin(startAngle) * useInnerRadius;
      const innerEndX = centerX + Math.cos(endAngle) * useInnerRadius;
      const innerEndY = centerY + Math.sin(endAngle) * useInnerRadius;
      
      // Create path
      const largeArcFlag = angleSize > Math.PI ? 1 : 0;
      const path = [
        `M ${startX} ${startY}`,
        `A ${useOuterRadius} ${useOuterRadius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
        `L ${innerEndX} ${innerEndY}`,
        `A ${useInnerRadius} ${useInnerRadius} 0 ${largeArcFlag} 0 ${innerStartX} ${innerStartY}`,
        'Z'
      ].join(' ');
      
      // Calculate label position
      const labelAngle = startAngle + (angleSize / 2);
      
      // Adjust label position based on whether it's inner or outer ring
      const labelRadius = isOuterRing ? useOuterRadius * 1.15 : useOuterRadius * 0.8;
      
      const labelX = centerX + Math.cos(labelAngle) * labelRadius;
      const labelY = centerY + Math.sin(labelAngle) * labelRadius;
      
      // Determine colors - use a different scheme for inner vs outer rings
      let fill = "#333333";
      let fillOpacity = 0.9;

      if (isOuterRing) {
        // Outer ring colors
        const colorValues = [
          "var(--accent)",
          "rgb(30, 30, 30)",
          "rgb(80, 80, 80)",
          "rgb(130, 130, 130)",
          "rgb(180, 180, 180)",
          "rgb(210, 210, 210)"
        ];
        fill = colorValues[index % colorValues.length];
        fillOpacity = index === 0 ? 0.9 : 0.8; // Accent color gets full opacity
      } else {
        // Inner ring represents AI vs non-AI split
        // Only two segments for inner ring - AI (dark red) and non-AI (light blue)
        if (index === 0) {
          fill = "#aa0000"; // Dark red for AI-relevant
          fillOpacity = 0.9;
        } else {
          fill = "#4682B4"; // Light blue for non-AI
          fillOpacity = 0.7;
        }
      }

      // Process multi-line labels
      const multiLineLabel = item.label.split('\n');
      
      segments.push({
        path,
        fill,
        labelX,
        labelY,
        labelAngle,
        label: item.label,
        multiLineLabel: multiLineLabel.length > 1 ? multiLineLabel : undefined,
        fillOpacity
      });
      
      currentAngle += angleSize;
    });
    
    return segments;
  };

  // Generate curved connecting lines
  const generateConnectingLines = () => {
    const lines: ConnectingLine[] = [];
    const colors = ["rgb(100, 0, 0)", "var(--accent)", "rgb(100, 0, 0)"];
    const labels = ["1.6x production, 1x share", "1.1x production, 1.5x share", "1.6x production, 1x share"];
    
    // Create a curved line from each left chart to the right chart
    [leftCenterY1, leftCenterY2, leftCenterY3].forEach((startY, index) => {
      // Calculate control points for a smooth curve
      const startX = leftCenterX + chartRadius;
      const endX = rightCenterX - chartRadius;
      const endY = rightCenterY;
      
      // Control points (adjust these for curve shape)
      const controlX1 = startX + (endX - startX) * 0.3;
      const controlY1 = startY;
      const controlX2 = startX + (endX - startX) * 0.7;
      const controlY2 = endY;
      
      // Create Bezier curve path
      const path = `M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`;
      
      // Calculate label position (middle of the curve)
      const labelX = startX + (endX - startX) / 2;
      const labelY = startY + (endY - startY) / 2 - 10; // Slight offset for visibility
      
      lines.push({
        path,
        color: colors[index % colors.length],
        label: labels[index],
        labelX,
        labelY
      });
    });
    
    return lines;
  };

  // Generate all segments and lines
  const outerSegmentsLeft1 = generatePieSegments(dataLeft1, leftCenterX, leftCenterY1, true, false);
  const innerSegmentsLeft1 = generatePieSegments(innerRingData1, leftCenterX, leftCenterY1, false, false);
  
  const outerSegmentsLeft2 = generatePieSegments(dataLeft2, leftCenterX, leftCenterY2, true, false);
  const innerSegmentsLeft2 = generatePieSegments(innerRingData2, leftCenterX, leftCenterY2, false, false);
  
  const outerSegmentsLeft3 = generatePieSegments(dataLeft3, leftCenterX, leftCenterY3, true, false);
  const innerSegmentsLeft3 = generatePieSegments(innerRingData3, leftCenterX, leftCenterY3, false, false);
  
  const outerSegmentsRight = generatePieSegments(dataRight, rightCenterX, rightCenterY, true, true);
//   const innerSegmentsRight = generatePieSegments(innerRingDataRight, rightCenterX, rightCenterY, false, false);
  
  const connectingLines = generateConnectingLines();

  // Calculate center label positions
  const centerLabels = [
    { x: leftCenterX, y: leftCenterY1, label: "HBM" },
    { x: leftCenterX, y: leftCenterY2, label: "Wafers" },
    { x: leftCenterX, y: leftCenterY3, label: "Packaging" },
    { x: rightCenterX, y: rightCenterY, label: "" }
  ];

  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Main Chart Title */}
        <text
          x={margin.left}
          y={40}
          fontSize="28"
          fontFamily="monospace"
          className="text-[28px] font-mono md:text-[24px] sm:text-[20px]"
          fill="#333333"
        >
          AI Chip Production Forecast Summary, 2025-2027
        </text>
        
        {/* Chart Titles */}
        {chartTitles.map((title, i) => (
          <text
            key={`title-${i}`}
            x={title.x}
            y={title.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="20"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            {title.title}
          </text>
        ))}
        
        {/* Draw connecting lines first (so they're behind the pie charts) */}
        {connectingLines.map((line, i) => {
        // Manually defined rotation angles for each line
        const rotationAngles = [37.5, 0, -37.5]; // Adjust these values as needed
        const leftShift = [12, 22, 12];
        const upShift = [3, 1, 3];
        return (
            <g key={`line-${i}`}>
            <path
                d={line.path}
                fill="none"
                stroke={line.color}
                strokeWidth="3"
                strokeOpacity="0.7"
            />
            <text
                x={line.labelX-leftShift[i]}
                y={line.labelY-upShift[i]}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={line.color}
                fontSize="14"
                fontFamily="monospace"
                fontWeight="bold"
                transform={`rotate(${rotationAngles[i]}, ${line.labelX}, ${line.labelY})`}
            >
                {line.label}
            </text>
            </g>
        );
        })}   
        
        {/* Draw all pie chart segments */}
        {/* Left Chart 1 (Top) - Outer Ring */}
        {outerSegmentsLeft1.map((segment, i) => (
          <path
            key={`segment-left1-outer-${i}`}
            d={segment.path}
            fill={segment.fill}
            fillOpacity={segment.fillOpacity}
            stroke="#ffffff"
            strokeWidth=".5"
          />
        ))}
        
        {/* Left Chart 1 (Top) - Inner Ring */}
        {innerSegmentsLeft1.map((segment, i) => (
          <path
            key={`segment-left1-inner-${i}`}
            d={segment.path}
            fill={segment.fill}
            fillOpacity={segment.fillOpacity}
            stroke="#ffffff"
            strokeWidth=".5"
          />
        ))}
        
        {/* Left Chart 2 (Middle) - Outer Ring */}
        {outerSegmentsLeft2.map((segment, i) => (
          <path
            key={`segment-left2-outer-${i}`}
            d={segment.path}
            fill={segment.fill}
            fillOpacity={segment.fillOpacity}
            stroke="#ffffff"
            strokeWidth=".5"
          />
        ))}
        
        {/* Left Chart 2 (Middle) - Inner Ring */}
        {innerSegmentsLeft2.map((segment, i) => (
          <path
            key={`segment-left2-inner-${i}`}
            d={segment.path}
            fill={segment.fill}
            fillOpacity={segment.fillOpacity}
            stroke="#ffffff"
            strokeWidth=".5"
          />
        ))}
        
        {/* Left Chart 3 (Bottom) - Outer Ring */}
        {outerSegmentsLeft3.map((segment, i) => (
          <path
            key={`segment-left3-outer-${i}`}
            d={segment.path}
            fill={segment.fill}
            fillOpacity={segment.fillOpacity}
            stroke="#ffffff"
            strokeWidth=".5"
          />
        ))}
        
        {/* Left Chart 3 (Bottom) - Inner Ring */}
        {innerSegmentsLeft3.map((segment, i) => (
          <path
            key={`segment-left3-inner-${i}`}
            d={segment.path}
            fill={segment.fill}
            fillOpacity={segment.fillOpacity}
            stroke="#ffffff"
            strokeWidth=".5"
          />
        ))}
        
        {/* Right Chart - Outer Ring */}
        {outerSegmentsRight.map((segment, i) => (
          <path
            key={`segment-right-outer-${i}`}
            d={segment.path}
            fill={segment.fill}
            fillOpacity={segment.fillOpacity}
            stroke="#ffffff"
            strokeWidth=".5"
          />
        ))}
        
        {/* Right Chart - Inner Ring */}
        {/* {innerSegmentsRight.map((segment, i) => (
          <path
            key={`segment-right-inner-${i}`}
            d={segment.path}
            fill={segment.fill}
            fillOpacity={segment.fillOpacity}
            stroke="#ffffff"
            strokeWidth=".5"
          />
        ))} */}
        
        {/* Labels for Left Chart 1 (Top) */}
        {outerSegmentsLeft1.map((segment, i) => (
          <text
            key={`label-left1-${i}`}
            x={segment.labelX}
            y={segment.labelY}
            textAnchor={Math.cos(segment.labelAngle) > 0 ? "start" : "end"}
            dominantBaseline="middle"
            className="text-[14px] font-mono md:text-[12px] sm:text-[10px]"
            fill="#333333"
          >
            {segment.label}
          </text>
        ))}
        
        {/* Labels for Left Chart 2 (Middle) */}
        {outerSegmentsLeft2.map((segment, i) => (
          <text
            key={`label-left2-${i}`}
            x={segment.labelX}
            y={segment.labelY}
            textAnchor={Math.cos(segment.labelAngle) > 0 ? "start" : "end"}
            dominantBaseline="middle"
            className="text-[14px] font-mono md:text-[12px] sm:text-[10px]"
            fill="#333333"
          >
            {segment.label}
          </text>
        ))}
        
        {/* Labels for Left Chart 3 (Bottom) */}
        {outerSegmentsLeft3.map((segment, i) => (
          <text
            key={`label-left3-${i}`}
            x={segment.labelX}
            y={segment.labelY}
            textAnchor={Math.cos(segment.labelAngle) > 0 ? "start" : "end"}
            dominantBaseline="middle"
            className="text-[14px] font-mono md:text-[12px] sm:text-[10px]"
            fill="#333333"
          >
            {segment.label}
          </text>
        ))}
        
        {/* Labels for Right Chart */}
        {outerSegmentsRight.map((segment, i) => (
          <text
            key={`label-right-${i}`}
            x={segment.labelX}
            y={segment.labelY}
            textAnchor={Math.cos(segment.labelAngle) > 0 ? "start" : "end"}
            dominantBaseline="middle"
            className="text-[14px] font-mono md:text-[12px] sm:text-[10px]"
            fill="#333333"
          >
            {segment.label}
          </text>
        ))}
        
        {/* Center labels for each pie chart */}
        {centerLabels.map((centerLabel, i) => (
          <text
            key={`center-label-${i}`}
            x={centerLabel.x}
            y={centerLabel.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="16"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            {centerLabel.label}
          </text>
        ))}
        
        {/* Legend - only for inner rings AI vs non-AI */}
        <g transform={`translate(${width - 180}, 60)`}>
          <text
            fontSize="16"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            Legend
          </text>
          
          {/* Inner ring legend items */}
          <rect x="0" y="30" width="15" height="15" fill="#aa0000" fillOpacity="0.9" />
          <text x="25" y="42" fontSize="14" fontFamily="monospace" fill="#333333">AI-relevant</text>
          
          <rect x="0" y="55" width="15" height="15" fill="#4682B4" fillOpacity="0.7" />
          <text x="25" y="67" fontSize="14" fontFamily="monospace" fill="#333333">Non-AI</text>
        </g>
      </svg>
    </div>
  );
}